/**
 * The pieces every React Flow canvas is built from, so Architecture, the Git
 * branch map, the team map and a plan's graph read as one family:
 *
 *   FlowCanvas    the rounded frame, the dotted ground, the theme the app is
 *                 in (not the OS's), the zoom stack bottom-left and the
 *                 minimap bottom-right once there is enough to need one
 *   FlowBandNode  a group: a tinted band with a dot, a title and one line
 *   FlowViaEdge   an edge that goes round by the corners its layout found for
 *                 it (flowEdge's `via`) rather than behind a card; every
 *                 canvas knows it
 *   FlowRefit     fits the view again when the frame or the picture changes
 *                 shape, for a canvas whose graph is meant to fill its frame
 *   FlowCounts    the tinted count pills above a canvas, which double as its
 *                 legend when an entry carries a swatch of its edge colour
 *
 * The plain-data half (edges, band geometry, handle class) is flowStyle.ts.
 *
 * Ported from the workbench's components/flowCanvas.tsx: the theme is read here (its useIsDark), stillness is the
 * operating system's, and the plan-colour bands and the full-screen control, which the console has no use for, are left out.
 */
import '@xyflow/react/dist/style.css';
import './flowCanvas.css';

import { Background, BaseEdge, Controls, type Edge, type EdgeProps, type EdgeTypes, type FitViewOptions, MiniMap, type Node, type NodeProps, ReactFlow, type ReactFlowProps, useReactFlow, useStore } from '@xyflow/react';
import { type CSSProperties, type HTMLAttributes, type ReactNode, useEffect, useMemo, useState } from 'react';

import { MINIMAP } from './flowMinimap';
import { FLOW_FOOTER, FLOW_VIA_EDGE, type FlowBandData, type FlowPoint, flowViaPath, showMinimap } from './flowStyle';
import type { ArtTone } from './ui/Art.tsx';
import { FitText } from './ui/FitText.tsx';

/**
 * Whether the console shows its dark theme now: the `dark` class on `<html>` (theme.ts), never the operating
 * system's preference, so a canvas is never a black slab in a light window.
 */
export function useIsDark(): boolean {
  const [dark, setDark] = useState(() => typeof document !== 'undefined' && document.documentElement.classList.contains('dark'));
  useEffect(() => {
    const read = () => setDark(document.documentElement.classList.contains('dark'));
    read();
    const observer = new MutationObserver(read);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);
  return dark;
}

/** The person asked for stillness: nothing on a canvas moves. */
const prefersStillness = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/** An orthogonal edge like every other, by way of the corners in `data.via` (flowViaPath). */
export function FlowViaEdge({ id, sourceX, sourceY, targetX, targetY, data, style, markerEnd, interactionWidth, label, labelStyle, labelShowBg, labelBgStyle, labelBgPadding, labelBgBorderRadius }: EdgeProps<Edge<{ via?: readonly FlowPoint[] }>>) {
  const via = data?.via ?? [];
  // A label (a workflow's branch says which decision it follows) sits on the last corner, where the line turns to its card.
  const at = via[via.length - 1] ?? { x: (sourceX + targetX) / 2, y: (sourceY + targetY) / 2 };
  const labelled = label !== undefined && label !== null && label !== '' ? { label, labelX: at.x, labelY: at.y, labelStyle, labelShowBg, labelBgStyle, labelBgPadding, labelBgBorderRadius } : {};
  return <BaseEdge id={id} path={flowViaPath({ x: sourceX, y: sourceY }, { x: targetX, y: targetY }, via)} style={style} markerEnd={markerEnd} interactionWidth={interactionWidth} {...labelled} />;
}

const FLOW_EDGE_TYPES: EdgeTypes = { [FLOW_VIA_EDGE]: FlowViaEdge };

export function FlowCanvas<N extends Node = Node, E extends Edge = Edge>({
  frameClassName,
  frameStyle,
  bare,
  minimap,
  minimapNodeColor,
  footer,
  edgeTypes,
  children,
  ...props
}: ReactFlowProps<N, E> & {
  /** Classes for the frame: its size in the layout (`flex-1`, `h-full`). */
  frameClassName?: string;
  /** For a frame sized to its content, which no class can say. */
  frameStyle?: CSSProperties;
  /** No border or radius of its own, for a canvas that is already the body of a section card. */
  bare?: boolean;
  /** Overrule the shared rule (showMinimap) for a canvas that knows better; left out, the rule decides. */
  minimap?: boolean;
  /** A card's colour in the overview — its state, say. Undefined (and every band) falls back to the shared grey. Only tokens every theme defines: see flowMinimap.ts. */
  minimapNodeColor?: (node: N) => string | undefined;
  /**
   * For a canvas that keeps a strip clear along its bottom edge (see
   * FLOW_FOOTER in flowStyle.ts) so nothing is ever drawn over a card: the
   * zoom buttons lie down in a row and the overview is cut to the strip's height.
   */
  footer?: boolean;
  children?: ReactNode;
}) {
  const isDark = useIsDark();
  const allEdgeTypes = useMemo(() => (edgeTypes ? { ...FLOW_EDGE_TYPES, ...edgeTypes } : FLOW_EDGE_TYPES), [edgeTypes]);
  const overviewColor = useMemo(() => (minimapNodeColor ? (node: N) => (node.type === 'band' ? undefined : minimapNodeColor(node)) ?? MINIMAP.nodeColor(node) : MINIMAP.nodeColor), [minimapNodeColor]);
  return (
    <div className={`flow-frame ${bare ? 'flow-frame--bare' : ''} ${frameClassName ?? ''}`} style={frameStyle}>
      <ReactFlow<N, E> proOptions={{ hideAttribution: true }} colorMode={isDark ? 'dark' : 'light'} nodesConnectable={false} edgeTypes={allEdgeTypes} {...props}>
        <Background gap={16} />
        {(minimap ?? showMinimap(props.nodes)) ? <MiniMap<N> pannable zoomable {...MINIMAP} nodeColor={overviewColor} style={footer ? FLOW_FOOTER.minimap : undefined} /> : null}
        <Controls showInteractive={false} orientation={footer ? 'horizontal' : 'vertical'} />
        {children}
      </ReactFlow>
    </div>
  );
}

/**
 * `fitView` fits once, on mount. A canvas whose graph is laid out to fill its
 * frame puts this inside its FlowCanvas to fit again whenever the frame is
 * resized (as React Flow itself measures it, so never against a stale size)
 * or `shape` — whatever the canvas says identifies its picture — changes.
 * Not on every render: a status tick must not undo the user's pan and zoom.
 * The move is animated unless the user asked for stillness.
 */
export function FlowRefit({ options, shape }: { options: FitViewOptions; shape: string }) {
  const { fitView } = useReactFlow();
  const width = useStore((s) => s.width);
  const height = useStore((s) => s.height);
  useEffect(() => {
    if (!width || !height) return;
    // The OS's reduced motion, or Settings → Appearance → Reduce effects.
    const still = prefersStillness();
    const raf = requestAnimationFrame(() => void fitView({ ...options, duration: still ? 0 : 220 }));
    return () => cancelAnimationFrame(raf);
  }, [width, height, shape, options, fitView]);
  return null;
}

/** A group band (see flowBand in flowStyle.ts). */
export function FlowBandNode({ data }: NodeProps<Node<FlowBandData>>) {
  return (
    <div
      className="card-art flow-band h-full w-full rounded-2xl"
      data-tone={data.tone ?? 'slate'}
      data-motif="arc"
    >
      {/* The band is transparent to the pointer so the canvas pans across it; its head is not, so a
          title or description that does not fit shows the rest on hover. The description wraps to
          two lines, which BAND_HEAD leaves room for. */}
      <div className="pointer-events-auto px-4 pt-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
          {data.icon ? <span className="flex size-3.5 shrink-0 items-center text-muted [&>svg]:size-3.5" aria-hidden="true">{data.icon}</span> : <span className="flow-band__dot" aria-hidden="true" />}
          <FitText className="min-w-0">{data.title}</FitText>
        </div>
        {data.description ? (
          <FitText as="div" lines={2} className="text-xs leading-4 text-muted">
            {data.description}
          </FitText>
        ) : null}
      </div>
    </div>
  );
}

export interface FlowCount {
  key: string;
  label: ReactNode;
  /** The number, in bold; left out for a pure legend entry. */
  n?: number;
  tone: ArtTone;
  /** A short line in the tone, for an entry that names an edge colour. */
  swatch?: 'line' | 'dashed';
  title?: string;
}

export function FlowCounts({ items, className, ...rest }: { items: readonly FlowCount[] } & HTMLAttributes<HTMLSpanElement>) {
  return (
    <span className={`flex flex-wrap items-center gap-1.5 ${className ?? ''}`} {...rest}>
      {items.map((item) => (
        <span key={item.key} className="flow-count" data-tone={item.tone} title={item.title}>
          {item.swatch ? <span className="flow-count__swatch" data-dashed={item.swatch === 'dashed' ? '' : undefined} aria-hidden="true" /> : null}
          {item.n !== undefined ? <span className="flow-count__n">{item.n.toLocaleString()}</span> : null}
          <span>{item.label}</span>
        </span>
      ))}
    </span>
  );
}
